import React from 'react'

import { FormProvider, useForm } from 'react-hook-form'
import { GPWebPay } from '@sga/shopify-payments-types'

import {
  Banner,
  Button,
  Card,
  Form,
  FormLayout,
  Heading,
  Stack,
  TextContainer,
  TextStyle,
} from '@shopify/polaris'

import { createApi } from '../api'
import { segment } from '../util/createSegmentPicker'
import { getTranslation } from '../util/getTranslation'
import { useAuth } from './AuthContext'
import { FormTextField } from './FormTextField'
import { FormSelectField } from './FormSelectField'

type TProps = {
  onSuccess(): void
}

GPWebPayCredentialsForm.segment = segment('gpwebpay')

export function GPWebPayCredentialsForm({ onSuccess }: TProps) {
  const config = useAuth()
  const {
    segmentCredentials,
    segmentConfig: { testCredentials },
  } = config.account as GPWebPay.TSegmentAccount
  const form = useForm<GPWebPay.TSegmentCredentials>({
    reValidateMode: 'onBlur',
  })

  const [testMode, setTestMode] = React.useState(true)
  const [status, setStatus] = React.useState<'init' | 'failed' | 'done'>('init')

  React.useEffect(() => {
    if (testMode) {
      form.reset({
        merchantNumber: testCredentials?.merchantNumber,
        privateKey: testCredentials?.privateKey,
        privateKeyPass: testCredentials?.privateKeyPass,
        providerCode: testCredentials?.providerCode || '0880',
      })
    } else {
      form.reset({
        merchantNumber: segmentCredentials.merchantNumber,
        privateKey: segmentCredentials.privateKey,
        privateKeyPass: segmentCredentials.privateKeyPass,
        providerCode: segmentCredentials.providerCode || '0880',
      })
    }
  }, [testMode, form, segmentCredentials, testCredentials])

  const onSubmit = form.handleSubmit(async (values) => {
    setStatus('init')
    const api = createApi()
    const { success } = await api.verifyCredentials(values, testMode)
    if (success) {
      setStatus('done')
      if (testMode) {
        setTestMode(false)
      } else {
        onSuccess()
      }
    } else {
      setStatus('failed')
    }
  })

  return (
    <Card sectioned title={getTranslation('gpwebpay.credentials_title')}>
      <Stack vertical>
        <TextContainer>
          <p>
            {getTranslation('gpwebpay.credentials_description')}
          </p>
          <p>
            <TextStyle variation="strong">
              {testMode
                ? getTranslation('gpwebpay.test_credentials_instruction')
                : getTranslation('gpwebpay.production_credentials_instruction')}
            </TextStyle>
          </p>
        </TextContainer>
        <FormProvider {...form}>
          <Form onSubmit={onSubmit}>
            <FormLayout>
              <FormTextField name="merchantNumber" label={getTranslation('fields.merchant_number')} validationPattern={/^[0-9]+$/} />
              <FormTextField name="privateKey" label={getTranslation('fields.private_key')} />
              <FormTextField name="privateKeyPass" label={getTranslation('fields.private_key_password')} />
              <FormSelectField
                name="providerCode"
                label={getTranslation('fields.provider_code')}
                options={[
                  { value: "0880", label: "Global Payments CZ" },
                  { value: "0902", label: "Global Payments SK" },
                  { value: "0870", label: "Global Payments RO" },
                  { value: "0910", label: "Global Payments AT" },
                  { value: "8276", label: "Commerz Globalpay DE" },
                  { value: "7500", label: "ČSOB SK" },
                ]}
              />
              <Stack distribution="trailing">
                <Button primary submit disabled={form.formState.isSubmitting}>
                  {testMode
                    ? getTranslation('gpwebpay.save_test_credentials')
                    : getTranslation('gpwebpay.save_production_credentials')}
                </Button>
              </Stack>
              {status === 'failed' && (
                <Banner status="critical">
                  <Heading>
                    {getTranslation('gpwebpay.verification_failed')}
                  </Heading>
                  <p>
                    {getTranslation('gpwebpay.verification_failed_description')}
                  </p>
                </Banner>
              )}
            </FormLayout>
          </Form>
        </FormProvider>
        {/* <TextContainer>
          <Heading>{getTranslation('GoPay účet ještě nemáte?', "Don't have a GoPay account yet?")}</Heading>
          <p>
            {getTranslation(
              <>
                Je potřeba{' '}
                <Link url={registrationFormUrl} external>
                  vyplnit formulář společnosti GoPay
                </Link>{' '}
                a jakmile budete mít potřebné údaje, použijte stejný odkaz, kterým jste se dostali na tuto stránku.
              </>,
              <>
                You have to{' '}
                <Link url={registrationFormUrl} external>
                  fill in the GoPay form
                </Link>{' '}
                and as soon as you have all the information required, use the same link which has lead you to this page.
              </>,
            )}
          </p>
        </TextContainer> */}
      </Stack>
    </Card>
  )
}
